import React from "react";
import { Mail, Server, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_BADGE, ROLE_LABELS } from "@/lib/stages";
import { FreshStart } from "@/components/FreshStart";
import { TrustedSenders } from "@/components/TrustedSenders";

const ROLE_CAPABILITIES = [
  ["admin", "Full system access. Manages all users, settings, and acts on all stages."],
  ["broker_manager", "Supervisory. Full visibility, reassigns shipments, escalates issues, acts on all stages."],
  ["broker_csr", "Handles client inquiries and documentation. Updates checklists, generates status reports."],
  ["declarant", "Prepares and lodges customs declarations. HS codes, duties/taxes, BOC references."],
  ["forwarder", "Transport & logistics. Carrier info, bookings, dispatch schedules, tracking."],
  ["processor", "Submits physical docs to BOC/BIR/DTI. Records release orders and gate passes."],
  ["delivery", "Final delivery. Trucking details, delivery status, POD upload."],
  ["finance_staff", "Records invoices, dues and collections. Prepares fund requests, cash and check vouchers, and liquidations. Receives and acknowledges Billing Transmittals, then creates the invoice in Finance."],
  ["finance_manager", "Everything Finance Staff can do, plus approves fund requests, cash vouchers, check vouchers and liquidations. Full visibility of Finance."],
  ["sales", "Client acquisition, quotations, access to client shipments."],
];

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Account</div>
        <h1 className="font-display text-4xl font-bold">Settings</h1>
      </div>

      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center gap-2 text-slate-200 font-display text-xl mb-4">
          <Shield size={18} className="text-sky-400" /> Profile
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Name</dt><dd>{user?.name}</dd></div>
          <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Email</dt><dd className="font-mono-ops">{user?.email}</dd></div>
          <div>
            <dt className="text-slate-500 text-xs uppercase tracking-widest">Role</dt>
            <dd><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${ROLE_BADGE[user?.role] || ""}`}>{ROLE_LABELS[user?.role] || user?.role}</span></dd>
          </div>
        </dl>
      </div>

      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center gap-2 text-slate-200 font-display text-xl mb-4">
          <Mail size={18} className="text-amber-400" /> Email Notifications
        </div>
        <p className="text-sm text-slate-400 mb-3">
          SMTP for outgoing notifications and IMAP for inbound pre-alert monitoring are configured via environment variables on the server.
        </p>
        <div className="bg-slate-950/70 border border-slate-800 rounded-md p-4 text-xs font-mono-ops text-slate-300 space-y-1">
          <div>SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD, SMTP_FROM</div>
          <div>IMAP_HOST, IMAP_PORT, IMAP_USER, IMAP_PASSWORD, IMAP_FOLDER</div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Until SMTP is wired, outgoing emails are logged by the backend. The pre-alert inbox is checked every 15 minutes once the IMAP details are set; the "Check inbox now" button on the Shipments page runs a check on demand.
        </p>
      </div>

      {user?.role === "admin" && <TrustedSenders />}
      {user?.role === "admin" && <FreshStart />}

      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center gap-2 text-slate-200 font-display text-xl mb-4">
          <Server size={18} className="text-emerald-400" /> Role Capabilities
        </div>
        <ul className="text-sm text-slate-300 space-y-2">
          {ROLE_CAPABILITIES.map(([k, desc]) => (
            <li key={k} className="flex items-start gap-3">
              <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${ROLE_BADGE[k] || ""}`}>{ROLE_LABELS[k]}</span>
              <span className="text-slate-300">{desc}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
